import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    id: 1,
    category: "Genel",
    question: "DersMaster nedir ve nasıl çalışır?",
    answer: "DersMaster, öğrenciler ve eğitmenleri bir araya getiren özel ders ilan platformudur. Eğitmenler kendi uzmanlık alanlarında ders ilanları paylaşır ve müsait oldukları zamanları belirtir. Öğrenciler ilanlara talep oluşturur, eğitmen onaylarsa iletişime geçerek dersi gerçekleştirirler."
  },
  {
    id: 2,
    category: "Kayıt",
    question: "Nasıl kayıt olabilirim?",
    answer: "Ana sayfadaki 'Kayıt Ol' butonuna tıklayarak öğrenci veya eğitmen olarak kayıt olabilirsiniz. E-posta doğrulaması yapıldıktan sonra profilinizi tamamlayarak platformu kullanmaya başlayabilirsiniz."
  },
  {
    id: 3,
    category: "Eğitmenlik",
    question: "Eğitmen olarak nasıl ilan oluştururum?",
    answer: "Eğitmen hesabınızla giriş yaptıktan sonra 'İlan Oluştur' butonuna tıklayın. Ders konusunu, seviyesini, süresini ve müsait olduğunuz tarih/saatleri belirtin. İlanınız yayınlandıktan sonra öğrenciler talep oluşturabilir."
  },
  {
    id: 4,
    category: "Eğitmenlik",
    question: "Takvimimi nasıl güncellerim?",
    answer: "Profil sayfanızdan 'Takvim' bölümüne giderek müsait olduğunuz tarih ve saatleri ekleyebilir veya değiştirebilirsiniz. Takvim güncellemeleriniz anlık olarak ilanlarınıza yansır."
  },
  {
    id: 5,
    category: "Öğrencilik",
    question: "Ders talebi nasıl oluştururum?",
    answer: "İstediğiniz ilana tıklayarak detaylarını inceleyin. Uygun bir tarih/saat seçin ve 'Talep Oluştur' butonuna tıklayın. Eğitmen talebinizi onaylarsa iletişim bilgileri paylaşılır."
  },
  {
    id: 6,
    category: "Öğrencilik",
    question: "Talebim onaylanmadı, ne yapmalıyım?",
    answer: "Eğitmen çeşitli nedenlerle talebinizi reddedebilir (müsaitlik durumu, seviye uygunluğu vb.). Başka eğitmenlerin benzer ilanlarına talep oluşturabilir veya farklı tarih/saatler için tekrar deneyebilirsiniz."
  },
  {
    id: 7,
    category: "İletişim",
    question: "Eğitmenle nasıl iletişime geçerim?",
    answer: "Talebiniz onaylandıktan sonra eğitmenin iletişim bilgileri (telefon, e-posta, WhatsApp vb.) size gönderilir. Ders detaylarını konuşmak için doğrudan iletişime geçebilirsiniz."
  },
  {
    id: 8,
    category: "Ödeme",
    question: "Ödeme nasıl yapılır?",
    answer: "Şu anda platform üzerinden ödeme sistemi bulunmamaktadır. Ders ücretini eğitmenle doğrudan görüşerek belirlersiniz. Ödeme nakit, havale veya eğitmenin tercih ettiği yöntemle yapılabilir."
  },
  {
    id: 9,
    category: "Güvenlik",
    question: "Platformun güvenliği nasıl sağlanıyor?",
    answer: "Tüm eğitmenler kayıt sırasında kimlik doğrulamasından geçer. Ayrıca değerlendirme sistemi sayesinde güvenilir eğitmenleri kolayca bulabilirsiniz. Şüpheli durumları destek ekibimize bildirebilirsiniz."
  },
  {
    id: 10,
    category: "Teknik",
    question: "Teknik sorun yaşadığımda nasıl destek alabilirim?",
    answer: "Destek ekibimize destek@dersmaster.com adresinden ulaşabilir veya iletişim sayfamızdan mesaj gönderebilirsiniz. Genellikle 24 saat içinde yanıt vermeye çalışıyoruz."
  }
];

const categories = ["Tümü", ...new Set(faqs.map(faq => faq.category))];

const Faq = () => {
  const [openId, setOpenId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("Tümü");

  const filteredFaqs = selectedCategory === "Tümü" 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFaq = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* SEO optimized header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <QuestionMarkCircleIcon className="h-16 w-16 mx-auto mb-4 text-blue-100" />
          <h1 className="text-4xl font-bold mb-4">Sıkça Sorulan Sorular</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            DersMaster özel ders platformu hakkında merak ettiğiniz her şeyin cevabını burada bulabilirsiniz
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4">
        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {filteredFaqs.map((faq) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div>
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300 rounded-full mb-2">
                    {faq.category}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {faq.question}
                  </h3>
                </div>
                {openId === faq.id ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              
              <AnimatePresence>
                {openId === faq.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-12 text-center bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Cevap bulamadınız mı?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
            Destek ekibimiz size yardımcı olmaya hazır. Sorularınızı bize iletin, en kısa sürede yanıtlayalım.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
            >
              Destek Ekibine Ulaşın
            </a>
            <a
              href="/help"
              className="inline-block px-6 py-3 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-300 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
            >
              Yardım Merkezi
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faq;
